import { css, styled } from "@pigment-css/react";
import { useCallback, useState } from "react";
import { useTranslations } from "@acto/web-common/locales";
import type { TableDisplayData } from "@acto/taskpane/core/chat";

const tableTitleStyles = css(({ theme }) => ({
  fontSize: theme.font.size[30],
  lineHeight: theme.font.lineHeight[30],
  fontWeight: theme.font.weight.semiBold,
  color: theme.colors.neutral[900],
  marginBottom: theme.spacing[3],
}));

const tableDescriptionStyles = css(({ theme }) => ({
  fontSize: theme.font.size[20],
  lineHeight: theme.font.lineHeight[20],
  color: theme.colors.neutral[600],
  marginBottom: theme.spacing[4],
}));

const OuterWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${theme.colors.transparent[300]}`,
  borderRadius: theme.radius[6],
  overflowX: "auto",
}));

const TableRow = styled("div")(({ theme }) => ({
  padding: `${theme.spacing[3]} ${theme.spacing[3]}`,
  borderBottom: `1px solid ${theme.colors.transparent[300]}`,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  gap: theme.spacing[3],
  "&:last-child": {
    borderBottom: "none",
  },
}));

const ContentRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "50% 50%",
  alignItems: "start",
  width: "100%",
  fontSize: theme.font.size[30],
  lineHeight: theme.font.lineHeight[30],
  color: theme.colors.neutral[800],
}));

const CellKey = styled("span")(({ theme }) => ({
  color: theme.colors.neutral[600],
}));

const SeeMoreWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing[4],
  padding: theme.spacing[5],
}));

const ResultsText = styled("p")(({ theme }) => ({
  color: theme.colors.neutral[800],
  fontSize: theme.font.size[30],
  lineHeight: theme.font.lineHeight[30],
  fontWeight: theme.font.weight.medium,
  margin: 0,
}));

const SeeMoreButton = styled("button")(({ theme }) => ({
  padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
  border: `1px solid ${theme.colors.neutral[300]}`,
  borderRadius: theme.radius[4],
  backgroundColor: theme.colors.neutral[0],
  color: theme.colors.neutral[800],
  fontSize: theme.font.size[30],
  fontWeight: theme.font.weight.medium,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.colors.neutral[50],
  },
  "&:active": {
    backgroundColor: theme.colors.neutral[100],
  },
}));

const ErrorMessage = styled("div")(({ theme }) => ({
  color: theme.colors.status.error.main,
  fontSize: theme.font.size[20],
  padding: theme.spacing[4],
  border: `1px solid ${theme.colors.status.error[200]}`,
  borderRadius: theme.radius[4],
  backgroundColor: theme.colors.status.error[50],
}));

interface TableDisplayProps {
  data: TableDisplayData;
}

export function TableDisplay({ data }: TableDisplayProps) {
  const batchSize = 3;
  const [resultsShown, setResultsShown] = useState(batchSize);
  const tCommon = useTranslations("common");
  const tableId = data?.title ? `table-${data.title.replace(/\s+/g, "-").toLowerCase()}` : undefined;

  const showMoreResults = useCallback(() => {
    setResultsShown((prev) => prev + batchSize);
  }, []);

  if (!data || !data.title) {
    console.error("[TableDisplay] Error: Missing required title", data);
    return <ErrorMessage role="alert">{tCommon("tableLoadError")}</ErrorMessage>;
  }

  if (!Array.isArray(data.data)) {
    console.error("[TableDisplay] Error: data.data is not an array", data);
    return (
      <div>
        <div className={tableTitleStyles}>{data.title}</div>
        {data.description && <div className={tableDescriptionStyles}>{data.description}</div>}
        <ErrorMessage role="alert">{tCommon("tableLoadError")}</ErrorMessage>
      </div>
    );
  }

  if (data.data.length === 0) {
    console.warn("[TableDisplay] Warning: Empty data array for table:", data.title);
    return (
      <div>
        <div className={tableTitleStyles}>{data.title}</div>
        {data.description && <div className={tableDescriptionStyles}>{data.description}</div>}
        <ErrorMessage role="alert">{tCommon("noDataAvailable")}</ErrorMessage>
      </div>
    );
  }

  const cappedResults = data.data.slice(0, resultsShown);

  return (
    <div role="region" aria-labelledby={tableId}>
      <div className={tableTitleStyles} id={tableId}>
        {data.title}
      </div>
      {data.description && <div className={tableDescriptionStyles}>{data.description}</div>}
      <OuterWrapper role="table">
        {cappedResults.map((row, rowIndex) => {
          if (!row || typeof row !== "object") {
            console.error(`[TableDisplay] Error: Invalid row at index ${rowIndex}`, row);
            return (
              <TableRow key={rowIndex} role="row">
                <ErrorMessage role="alert">{tCommon("tableLoadError")}</ErrorMessage>
              </TableRow>
            );
          }

          const rowKeys = Object.keys(row);
          if (rowKeys.length === 0) {
            console.warn(`[TableDisplay] Warning: Empty row at index ${rowIndex}`);
            return null;
          }

          return (
            <TableRow key={rowIndex} role="row">
              {rowKeys.map((key) => {
                const cellData = row[key];
                return (
                  <ContentRow key={key}>
                    <CellKey role="rowheader">{key}</CellKey>
                    <span role="cell">{cellData !== null ? cellData : "N/A"}</span>
                  </ContentRow>
                );
              })}
            </TableRow>
          );
        })}
        {cappedResults.length < data.data.length && (
          <SeeMoreWrapper>
            <ResultsText aria-live="polite" aria-label="results-count">
              <strong>{Math.min(resultsShown, data.data.length)}</strong> of {data.data.length} results
            </ResultsText>
            <SeeMoreButton
              onClick={showMoreResults}
              aria-label={`Show more results (${resultsShown} of ${data.data.length} shown)`}
            >
              {tCommon("seeMore")}
            </SeeMoreButton>
          </SeeMoreWrapper>
        )}
      </OuterWrapper>
    </div>
  );
}
